import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-800', SENT: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800', VOID: 'bg-red-100 text-red-800', OVERDUE: 'bg-orange-100 text-orange-800',
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', paymentDate: new Date().toISOString().split('T')[0], method: 'BANK_TRANSFER', accountId: '', reference: '', notes: '' });

  const load = () => {
    api(`/invoices/${id}`).then(r => r.json()).then(setInvoice);
    api(`/invoices/${id}/payments`).then(r => r.json()).then(setPayments).catch(() => {});
  };

  useEffect(() => {
    load();
    api(`/accounts`).then(r => r.json()).then(setAccounts).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    await api(`/invoices/${id}`, { method: 'DELETE' });
    navigate('/accounting/invoices');
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    const body = { ...payForm, amount: parseFloat(payForm.amount) };
    const res = await api(`/invoices/${id}/payments`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) { setShowPayForm(false); setPayForm({ amount: '', paymentDate: new Date().toISOString().split('T')[0], method: 'BANK_TRANSFER', accountId: '', reference: '', notes: '' }); load(); }
  };

  const handleDeletePayment = async (paymentId) => {
    await api(`/invoices/${id}/payments/${paymentId}`, { method: 'DELETE' });
    load();
  };

  if (!invoice) return null;

  return (
    <Layout>
      <Link to="/accounting/invoices" className="text-emerald-600 hover:text-emerald-800 mb-4 inline-block">&larr; Back to Invoices</Link>
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
              <p className="text-gray-600">{invoice.companyName || invoice.contactName || ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${STATUS_COLORS[invoice.status]}`}>{invoice.status}</span>
              <Link to={`/accounting/invoices/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-2 sm:grid-cols-4 gap-6 border-b">
          <div><p className="text-sm text-gray-500 mb-1">Issue Date</p><p className="text-gray-900 font-medium">{invoice.issueDate}</p></div>
          <div><p className="text-sm text-gray-500 mb-1">Due Date</p><p className="text-gray-900 font-medium">{invoice.dueDate}</p></div>
          <div><p className="text-sm text-gray-500 mb-1">Total</p><p className="text-xl font-bold text-gray-900">${(invoice.total || 0).toFixed(2)}</p></div>
          <div><p className="text-sm text-gray-500 mb-1">Balance Due</p><p className={`text-xl font-bold ${(invoice.balanceDue || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(invoice.balanceDue || 0).toFixed(2)}</p></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {(invoice.lines || []).map((line) => (
                <tr key={line.id}>
                  <td className="px-6 py-4 whitespace-nowrap"><span className="font-mono text-sm text-gray-500">{line.accountCode}</span> {line.accountName}</td>
                  <td className="px-6 py-4 text-gray-600">{line.description || '—'}</td>
                  <td className="px-6 py-4 text-right">{line.quantity}</td>
                  <td className="px-6 py-4 text-right font-mono">{(line.unitPrice || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-right font-mono font-semibold">{(line.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {invoice.notes && <div className="p-4 md:p-6 border-t"><p className="text-sm text-gray-500 mb-1">Notes</p><p className="text-gray-900">{invoice.notes}</p></div>}
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Payments</h2>
          <button onClick={() => setShowPayForm(!showPayForm)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">Record Payment</button>
        </div>
        {showPayForm && (
          <form onSubmit={handlePayment} className="p-4 md:p-6 border-b bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" required value={payForm.amount} onChange={(e) => setPayForm({...payForm, amount: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" required value={payForm.paymentDate} onChange={(e) => setPayForm({...payForm, paymentDate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Method *</label><select value={payForm.method} onChange={(e) => setPayForm({...payForm, method: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"><option value="CASH">Cash</option><option value="CHECK">Check</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CREDIT_CARD">Credit Card</option><option value="OTHER">Other</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Deposit Account *</label><select required value={payForm.accountId} onChange={(e) => setPayForm({...payForm, accountId: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"><option value="">Select account</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference</label><input type="text" value={payForm.reference} onChange={(e) => setPayForm({...payForm, reference: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><input type="text" value={payForm.notes} onChange={(e) => setPayForm({...payForm, notes: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" /></div>
            </div>
            <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowPayForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button><button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save Payment</button></div>
          </form>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Reference</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Account</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th><th className="px-6 py-3"></th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map(p => (
                <tr key={p.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">{p.paymentDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{p.method.replace(/_/g, ' ')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{p.reference || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{p.accountName}</td>
                  <td className="px-6 py-4 text-right font-mono font-semibold text-green-600">{p.amount.toFixed(2)}</td>
                  <td className="px-6 py-4"><button onClick={() => handleDeletePayment(p.id)} className="text-red-500 hover:text-red-700 text-sm">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.length === 0 && <div className="text-center py-8 text-gray-500">No payments recorded.</div>}
        </div>
      </div>

      <ConfirmModal open={deleteModal} title="Delete Invoice" message="Are you sure you want to delete this invoice?" onConfirm={handleDelete} onCancel={() => setDeleteModal(false)} />
    </Layout>
  );
}
